import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export interface ViewerContext {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  container: HTMLElement;
  dispose: () => void;
  onResize: () => void;
}

/**
 * Create the three.js renderer, scene, camera, lights and controls.
 * The caller is responsible for adding/removing models and for eventually
 * calling `dispose()` when the component unmounts.
 */
export function createViewer(container: HTMLElement): ViewerContext {
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NoToneMapping;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x1a1d23);

  const camera = new THREE.PerspectiveCamera(35, 1, 0.01, 1000);
  camera.position.set(0, 1.2, 3);

  // Simple neutral lighting.
  const hemi = new THREE.HemisphereLight(0xffffff, 0x404040, 0.9);
  scene.add(hemi);

  const dir = new THREE.DirectionalLight(0xffffff, 0.8);
  dir.position.set(2, 3, 2);
  scene.add(dir);

  const ambient = new THREE.AmbientLight(0xffffff, 0.25);
  scene.add(ambient);

  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = 0.6;
  controls.maxDistance = 8;
  controls.target.set(0, 1, 0);

  const onResize = () => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };

  const ro = new ResizeObserver(onResize);
  ro.observe(container);
  onResize();

  // No lateral screen-space offset: we render straight through
  // OrbitControls so the mesh sits centred in the pane and its orbit
  // pivot visually coincides with the mesh itself.
  let rafId = 0;
  const tick = () => {
    controls.update();
    renderer.render(scene, camera);
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);

  const dispose = () => {
    cancelAnimationFrame(rafId);
    ro.disconnect();
    controls.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    if (renderer.domElement.parentElement === container) {
      container.removeChild(renderer.domElement);
    }
  };

  return { renderer, scene, camera, controls, container, dispose, onResize };
}

/**
 * Frame the given object in the camera view.
 *
 * This mirrors the centering logic from dcl-wearable-validator's
 * SimpleViewer: compute a world-space AABB by unioning each mesh's
 * own `geometry.boundingBox` transformed by its `matrixWorld`, then
 * point OrbitControls at the box center so rotation pivots around
 * the actual mesh centroid. Works identically for SkinnedMesh and
 * plain Mesh because we always use the mesh's own bind-space bbox.
 */
export type ViewSide = "front" | "back" | "left" | "right";

export function frameObject(
  object: THREE.Object3D,
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  paddingFactor = 1.0,
  side: ViewSide = "front",
): void {
  object.updateMatrixWorld(true);

  const box = computeWorldBounds(object);

  const center = new THREE.Vector3();
  const size = new THREE.Vector3();

  if (box.isEmpty() || !Number.isFinite(box.min.x)) {
    center.set(0, 0.9, 0);
    size.set(0.6, 1.8, 0.3);
  } else {
    box.getCenter(center);
    box.getSize(size);
    // DCL wearables are bilaterally symmetric about the world X=0
    // plane. Snap the pivot's X to 0 so sampling noise never pushes
    // the mesh off the viewer's centerline — and, more importantly,
    // so orbit rotation happens around the mesh itself. The lateral
    // "mesh appears on the left" look is handled as a render-time
    // camera offset in the tick loop (see FRAMING_X_SHIFT in
    // createViewer), NOT by moving the pivot off the mesh.
    center.x = 0;
  }

  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  // Same formula the validator uses: max(maxDim * 3, 1.5). The
  // paddingFactor multiplier lets callers zoom in/out without changing
  // the pivot.
  const distance = Math.max(maxDim * 3, 1.5) * paddingFactor;

  // The authored "front" of the DCL long-sleeve faces world +Z, so
  // placing the camera on the +Z side shows the chest panel instead of
  // the back panel. Left/right mirror the X axis so the snap buttons
  // match what the user sees on screen.
  let offset: THREE.Vector3;
  switch (side) {
    case "back":
      offset = new THREE.Vector3(0, 0, -distance);
      break;
    case "left":
      offset = new THREE.Vector3(+distance, 0, 0);
      break;
    case "right":
      offset = new THREE.Vector3(-distance, 0, 0);
      break;
    case "front":
    default:
      offset = new THREE.Vector3(0, 0, +distance);
      break;
  }

  camera.position.copy(center).add(offset);
  camera.near = Math.max(0.001, distance / 100);
  camera.far = distance * 100;
  camera.updateProjectionMatrix();
  camera.lookAt(center);

  // Pivot on the mesh centroid so rotating the view always orbits the
  // actual mesh center, exactly like the validator's SimpleViewer.
  controls.target.copy(center);
  controls.minDistance = distance * 0.1;
  controls.maxDistance = distance * 10;
  controls.update();
}

/**
 * World-space AABB of the *rendered* mesh.
 *
 * For a plain `Mesh` we transform its `geometry.boundingBox` by
 * `mesh.matrixWorld` (same as the validator's SimpleViewer).
 *
 * For a `SkinnedMesh` that shortcut does NOT work when the mesh node
 * itself sits outside the armature (typical for DCL wearable GLBs):
 * `mesh.matrixWorld` is identity but the geometry is in raw bind-pose
 * space — often ~100× the visible scale because the armature carries a
 * 0.01 scale. So we sample the geometry's vertex positions through the
 * actual skinning transform (`applyBoneTransform`), which gives the
 * true rendered world position for each vertex.
 */
function computeWorldBounds(root: THREE.Object3D): THREE.Box3 {
  const box = new THREE.Box3();
  const tmp = new THREE.Vector3();

  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;

    const pos = mesh.geometry.getAttribute("position") as
      | THREE.BufferAttribute
      | undefined;
    if (!pos) return;

    const skinned = mesh as unknown as THREE.SkinnedMesh & {
      applyBoneTransform?: (index: number, target: THREE.Vector3) => THREE.Vector3;
    };
    const isSkinned =
      skinned.isSkinnedMesh &&
      !!skinned.skeleton &&
      typeof skinned.applyBoneTransform === "function";

    if (isSkinned) {
      // Make sure the skeleton's bone matrices are fresh.
      skinned.skeleton.update();
    }

    // Subsample for big meshes; we only need a representative bbox.
    const count = pos.count;
    const step = count > 4096 ? Math.ceil(count / 4096) : 1;

    for (let i = 0; i < count; i += step) {
      tmp.fromBufferAttribute(pos, i);
      if (isSkinned) {
        skinned.applyBoneTransform!(i, tmp);
      }
      tmp.applyMatrix4(mesh.matrixWorld);
      box.expandByPoint(tmp);
    }
  });

  return box;
}

/**
 * Traverse and dispose GPU resources owned by the subtree.
 *
 * Skinning-safe:
 *   - Geometries and materials are disposed per Mesh/SkinnedMesh.
 *   - Bone nodes have no GPU resources of their own; we skip them.
 *   - We deduplicate geometries/materials/textures because skinned meshes
 *     often share resources (e.g. multiple primitives on one material).
 */
export function disposeObject(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();

  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;

    if (mesh.geometry) geometries.add(mesh.geometry);

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) if (m) materials.add(m);
  });

  for (const g of geometries) g.dispose();
  for (const m of materials) disposeMaterial(m);
}

function disposeMaterial(material: THREE.Material): void {
  const anyMat = material as unknown as Record<string, unknown>;
  for (const key of Object.keys(anyMat)) {
    const val = anyMat[key];
    if (val && (val as THREE.Texture).isTexture) {
      (val as THREE.Texture).dispose();
    }
  }
  material.dispose();
}
